import appConfig from 'app.config'
import { AccommodationType } from 'constants/enums/accommodationTypes'
import { AllCountries, euCountries } from 'constants/enums/countryTypes'
import { EmployedSincePeriod } from 'constants/enums/employedSincePeriod'
import { EmploymentType } from 'constants/enums/employmentTypes'
import { IdentificationType } from 'constants/enums/identificationType'
import { IncomePayslipsCount } from 'constants/enums/incomePayslipCount'
import { OccupationCategory } from 'constants/enums/occupationCategoryTypes'
import { PermitType } from 'constants/enums/permitTypes'
import { PropertyType } from 'constants/enums/propertyTypes'
import { ProvincesIt } from 'constants/enums/provinces'
import { PurposeOfLoan } from 'constants/enums/purposeOfLoanTypes'
import {
	employedOccupationCategories,
	getOccupationCategoryForOccupationType,
	getOccupationTypeOptions,
} from 'constants/values/italyOccupationCategories'
import { getIdentificationTypeOptions } from 'constants/values/italyIdentificationTypes'
import { yearsAgo, yearsInFuture } from 'formBuilder/src'
import { TranslationFile } from 'i18n'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { useTranslation } from 'react-i18next'
import { enumArrayToString, FormatEnumToList } from 'utils/commonhelpers'
import { GetAllCitizenshipsWithTranslation } from 'utils/countryHelpers'
import { formatToStringLocale } from 'utils/dateAndTimeHelpers'


// ProvincesIt's enum keys are what should be sent to the api; the values are just the display names
const provinceOptions = Object.entries(ProvincesIt).map(([key, name]) => ({
	value: key,
	label: name,
}))

// IncomePayslipsCount's keys are spelled-out numbers (Twelve, Thirteen, ...); the dropdown should show/send the numeric value (12, 13, ...) instead, so this bypasses FormatEnumToList (which would otherwise translate the spelled-out key).
const incomePayslipsCountOptions = Object.values(IncomePayslipsCount)
	.filter((count): count is number => typeof count === 'number')
	.map((count) => ({ value: count, label: count.toString() }))

export const ApplicationDetailsFormItStep1 = (
	data: IApplicationInformation,
) => {
	const { t } = useTranslation([TranslationFile.Forms])

	return {
		fieldSetName: '',
		formName: 'ApplicationDetailsForm1',
		description: t('forms:AddressDescription'),
		collapsable: false,
		show: true,
		visible: true,
		market: 'it',
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'nationality',
				type: 'dropdown',
				label: t('forms:Nationality'),
				value: data.personalInfo?.nationality,
				options: GetAllCitizenshipsWithTranslation(appConfig.country),
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownCountry'),
			},
			{
				required: true,
				controlName: 'streetType',
				type: 'text',
				label: t('forms:StreetType'),
				value: data?.address?.streetType,
				visible: true,
				col: 12,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^[A-Za-zÀ-ÖØ-öø-ÿ. ]{1,30}$',
						message: t('ValidationMsgUnsupportedChars'),
					},
				],
			},
			{
				required: true,
				controlName: 'street',
				type: 'text',
				label: t('forms:Street'),
				value: data?.address?.street,
				visible: true,
				col: 12,
				validations: [
					{
						code: 'MustMatchPattern',
						value: "^[A-Za-zÀ-ÖØ-öø-ÿ0-9'.\\- ]{1,100}$",
						message: t('ValidationMsgUnsupportedChars'),
					},
				],
			},
			{
				required: true,
				controlName: 'streetNumber',
				type: 'text',
				label: t('forms:StreetNumber'),
				value: data?.address?.streetNumber,
				visible: true,
				col: 12,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(?=.{1,10}$)[0-9]+[A-Za-z0-9/\\- ]*$',
						message: t('forms:ValidationMsgStreetNumber'),
					},
				],
			},
			{
				required: true,
				controlName: 'zip',
				type: 'text',
				label: t('forms:Zip'),
				value: data?.address?.zip,
				visible: true,
				col: 12,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(\\b\\d{5}\\b)$',
						message: t('ValidationMsgZip'),
					},
				],
			},
			{
				required: true,
				controlName: 'city',
				type: 'text',
				label: t('forms:City'),
				value: data?.address?.city,
				visible: true,
				col: 12,
				validations: [
					{
						code: 'MustMatchPattern',
						value: "^[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]{1,60}$",
						message: t('ValidationMsgUnsupportedChars'),
					},
				],
			},
			{
				required: true,
				controlName: 'province',
				type: 'dropdown',
				label: t('forms:Province'),
				value: data?.address?.province,
				options: provinceOptions,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownProvince'),
			},
			{
				required: true,
				readonly: true,
				controlName: 'countryOfResidence',
				type: 'text',
				label: t('forms:CountryOfResidence'),
				value: 'Italia',
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'accomodationSince',
				type: 'datePicker',
				minDate: yearsAgo(100),
				maxDate: new Date(), // today
				label: t('forms:SinceDate'),
				value: formatToStringLocale(data.accommodation?.since),
				visible: true,
				col: 12,
			},
		],
	}
}

export const ApplicationDetailsFormItStep2 = (
	data: IApplicationInformation,
) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
		TranslationFile.Countries,
	])
	const { t: tDropdown } = useTranslation(TranslationFile.Dropdown)
	const euCountriesList = enumArrayToString(AllCountries, euCountries, true)
	const initialOccupationCategory = getOccupationCategoryForOccupationType(
		data.employment?.occupationType,
	)
	const employedOccupationCategoriesList = enumArrayToString(
		OccupationCategory,
		employedOccupationCategories,
	)

	return {
		fieldSetName: '',
		formName: 'ApplicationDetailsForm2',
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'occupationCategory',
				type: 'dropdown',
				label: t('forms:OccupationCategory'),
				options: FormatEnumToList(
					OccupationCategory,
					PropertyType.OccupationCategory,
					TranslationFile.Dropdown,
				),
				value: initialOccupationCategory ?? '',
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationCategory'),
			},
			{
				required: true,
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				options: getOccupationTypeOptions(
					initialOccupationCategory,
					tDropdown,
				),
				value: data.employment?.occupationType,
				visible: false,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationType'),
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationCategory',
								rule: 'is not empty',
							},
							{
								field: 'occupationCategory',
								rule: 'not equal',
								value: 'TemporaryEmployee', // If category TemporaryEmployee is selected, the occupationType is not selectable but will be set to FixedTerm.
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'employmentType',
				type: 'dropdown',
				label: t('forms:EmploymentType'),
				options: FormatEnumToList(
					EmploymentType,
					PropertyType.EmploymentType,
					TranslationFile.Dropdown,
				),
				value: '',
				visible: false,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownEmploymentType'),
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationCategory',
								rule: 'in',
								value: employedOccupationCategoriesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'monthlyNetIncome',
				type: 'number',
				label: t('forms:Income'),
				value: data.employment?.monthlyNetIncome,
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'numberOfPayslips',
				type: 'dropdown',
				label: t('forms:NumberOfPayslips'),
				options: incomePayslipsCountOptions,
				value: data.employment?.numberOfPayslips,
				visible: false,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownNumberOfPayslips'),
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationCategory',
								rule: 'in',
								value: employedOccupationCategoriesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'employedSincePeriod',
				type: 'dropdown',
				label: t('forms:EmployedSince'),
				value: '',
				options: FormatEnumToList(
					EmployedSincePeriod,
					PropertyType.EmployedSincePeriod,
					TranslationFile.Dropdown,
				),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationCategory',
								rule: 'in',
								value: employedOccupationCategoriesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'childrenInFamily',
				type: 'text',
				label: t('forms:ChildrenInFamily'),
				description: t('forms:ChildrenInFamilyDescription'),
				validations: [
					{
						code: 'MustMatchPattern',
						value: '\\b^([0-9]|[1-9][0-9]|100)\\b$',
						message: t('forms:ValidationMsgChildrenInFamily'),
					},
				],
				value: data.accommodation?.childrenInFamily,
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'accomodationType',
				type: 'dropdown',
				label: t('forms:AccommodationType'),
				options: FormatEnumToList(
					AccommodationType,
					PropertyType.AccommodationType,
					TranslationFile.Dropdown,
				),
				value: data.accommodation?.type,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownAccomodation'),
			},
			{
				required: true,
				controlName: 'houseMonthlyCost',
				type: 'number',
				label: t('forms:MortgageMonthlyCost'),
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'identificationType',
				type: 'dropdown',
				label: t('forms:IdentificationType'),
				options: getIdentificationTypeOptions(
					data.personalInfo?.nationality,
					tDropdown,
				),
				value: '',
				visible: true,
				tooltip: t('forms:IdentificationTypeInfoDescription'),
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownIdentificationType'),
			},
			{
				required: true,
				controlName: 'identificationNumber',
				type: 'text',
				label: t('forms:IdentificationNumber'),
				col: 12,
				visible: true,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '\\b^[a-zA-Z0-9.,\\-_\\s]*$\\b',
						message: t('forms:ValidationMsgUnsupportedChars'),
					},
				],
			},
			{
				required: true,
				controlName: 'residencePermit',
				type: 'dropdown',
				label: t('forms:ResidencePermit'),
				options: FormatEnumToList(
					PermitType,
					PropertyType.PermitType,
					TranslationFile.Dropdown,
				),
				visible: false,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownResidencePermit'),
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'identificationType',
								rule: 'equal',
								value: IdentificationType.ResidenceTitle,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'residencePermitUntil',
				type: 'datePicker',
				minDate: new Date(), // today
				maxDate: yearsInFuture(100),
				label: t('forms:ResidencePermitUntil'),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'nationality',
								rule: 'is not empty',
							},
							{
								field: 'nationality',
								rule: 'not in',
								value: euCountriesList,
							},
							{
								field: 'residencePermit',
								rule: 'equal',
								value: 'Limited',
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'pep',
				type: 'dropdown',
				label: t('forms:PoliticallyExposedPerson'),
				value: '',
				options: [
					{
						value: false,
						label: t('No'),
					},
					{
						value: true,
						label: t('Yes'),
					},
				],
				visible: true,
				tooltip: t('forms:PEPInfoDescription'),
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownBoolean'),
			},
			{
				required: true,
				controlName: 'connectedToPep',
				type: 'dropdown',
				options: [
					{
						value: false,
						label: t('No'),
					},
					{
						value: true,
						label: t('Yes'),
					},
				],
				label: t('forms:ConnectedToPep'),
				value: '',
				visible: true,
				tooltip: t('forms:ConnectedToPepInfoDescription'),
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownBoolean'),
			},
			{
				required: true,
				controlName: 'purposeOfLoan',
				type: 'dropdown',
				label: t('forms:ApplicationPurposeOfLoan'),
				options: FormatEnumToList(
					PurposeOfLoan,
					PropertyType.PurposeOfLoan,
					TranslationFile.Dropdown,
				),
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownPurpose'),
			},
		],
	}
}

// Step1 and Step2 are combined into a single, multi-step form (rather than two independently registered forms) so that step2's conditions on 'nationality' can resolve it -- that control is only ever registered as part of step1, and this form-builder library only evaluates conditions against controls already registered in the SAME form/formName.
//
// `grid` is injected via dangerouslySetInnerHTML *inside* the <form id="main-form"> element that FormBuilder renders (see ContentWithScripts/FormStep), whereas the actual step controls/buttons are portaled into whichever element matches the "form-application-step" class. Almost all of _forms.scss's field/button styling is scoped under `#main-form`, so that portal target has to be this grid-injected div (a real DOM descendant of #main-form) rather than one rendered by the surrounding page component -- otherwise the portaled fields end up outside #main-form entirely and lose that styling.
const STEP_GRID = '<div class="form-application-step"></div>'

export const ApplicationDetailsFormItSteps = (data: IApplicationInformation) => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
	])

	return [
		{
			grid: STEP_GRID,
			backButton: '',
			nextButton: t('Next'),
			controls: [ApplicationDetailsFormItStep1(data)],
		},
		{
			grid: STEP_GRID,
			backButton: '',
			nextButton: t('Submit'),
			controls: [ApplicationDetailsFormItStep2(data)],
		},
	]
}
